import shortid from 'shortid';
import { get, isEmpty, merge, includes, isFunction, toString, isPlainObject, has } from 'lodash';

// local dependencies
import * as CORE_CONSTANTS from 'core/constants/message-bus-events';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import { Http } from 'core/modules/http';
import { addParamsToPath } from 'utils/addParamsToPath';
import { addLabelsAndUrlsOnStoreResolver as addLabelsAndUrlsOnStore } from 'resolvers/addLabelsAndUrlsOnStore';
import { formatHttpResponse } from 'resolvers/formatHttpResponse';

/*-----------------------*/

// create a new error class
export class HttpServiceError extends Error {
    constructor( errData, ...rest ) {
        super( ...rest );
        this.data = errData;
    }
}

/**
 * @desc HttpService provides an easy interface to communicate with the `http` module
 * and formats the request and reponse suitable for the consumption in the project.
 * @example
 * import { HttpService } from 'core/modules/network-service';
 *
 * export class UserInfoService extends HttpService {
 *   async fetchData( userId ) {
 *     try {
 *       return await this.get( {
 *         url: `/api/v1/user/${ userId }`
 *       } );
 *     } catch( errResponse ) {
 *       return this._errorFormatter( errResponse );
 *     }
 *   }
 *
 *   _errorFormatter( errResponse ) {
 *     return errResponse; // some logic
 *   }
 * }
 *
 * // create service instance and send/cancel request
 * const userInfoService = new UserInfoService();
 * const result = await userInfoService.fetchData( '<user-id>' ); // fetch data
 * userInfoService.cancel(); // cancel an active request
 */
export class HttpService {

    // on instantiate, generate a request id
    constructor() {
        this.id = shortid.generate();
    }

    /**
     * @desc Create a request configuration.
     * @param {*} method - HTTP method
     * @param {*} config - other configuration
     */
    makeRequestConfig( method, config ) {

        // default request configuration
        const defaultConfig = {
            requestId: this.id,
            host: 'http://localhost',
            path: '/',
            timeout: 5 * 60 * 1000, // 5 min
            withCredentials: true === CONFIG.enableAjaxWithCredentials,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json',
				'Content-Type': 'application/json',
				'Pragma': 'no-cache',
            }
        };

        // merge headers value
        if ( ! isEmpty( config.headers ) ) {
            defaultConfig.headers = {
                ...defaultConfig.headers,
                ...config.headers
            };
        }

        // add a upload progress listener
        if( config.uploadProgressCallBack && isFunction( config.uploadProgressCallBack ) ) {
            config.onUploadProgress = ( event ) => {
                config.uploadProgressCallBack( {
                    sent: event.loaded,
                    total: event.total,
                    percent: 0 !== event.total ? Math.round( ( event.loaded / event.total ) * 100 ) : null
                } );
            };
        }

        // add a download progress listener
        if( config.downloadProgressCallback && isFunction( config.downloadProgressCallback ) ) {
            config.onDownloadProgress = ( event ) => {
                config.downloadProgressCallback( {
                    received: event.loaded,
                    total: event.total,
                    percent: 0 !== event.total ? Math.round( ( event.loaded / event.total ) * 100 ) : null
                } );
            };
        }

        // return final request config
        if( 'string' === typeof config ) {
            return merge( {}, defaultConfig, { method: method, url: config } );
        } else {
            const _config = merge( {}, defaultConfig, config );

            if( ! isEmpty( _config.url ) ) {
                return merge( {}, _config, { method: method } );
            } else {
                return merge( {}, _config, { method: method, params: null, url: _config.host + addParamsToPath( _config.path, _config.params ) } );
            }
        }
    }

    /**
     * @desc Execute a network request.
     * @param {*} config - request config
     */
    async execute( config ) {

        // add request id to the config
        const _config = {
            ...config,
            requestId: this.id
        };

        // display message-bus event
        this.dispatchMBTransactionEvent( _config, { type: 'SENT', id: this.id } );

        // execute request from the `http` module
        try {
            const resp = await Http.execute( _config );
            return this.successResponseFormatter( _config, resp ); // return response
        } catch( errResp ) {
            throw new HttpServiceError( this.errorResponseFormatter( _config, errResp ) ); // throw error
        }
    }

    /**
     * @desc Formats the success-response of the network request.
     * @param {*} config - request config
     * @param {*} response - network response
     */
    successResponseFormatter( config, response ) {

        // send message-bus 'COMPLETE' event
        this.dispatchMBTransactionEvent( config, { type: 'SUCCESS', id: this.id } );

        this.dispatchNoPermissionsEvent( response );

        // add labels and urls to the store
        addLabelsAndUrlsOnStore( { response: response.data } );

        /*---------------------------------------------------*/

        return {
            status: response.status,
            body: formatHttpResponse( response.data ),
            headers: response.headers,
            timestamp: Date.now()
        };
    }

    /**
     * @desc Formats the error-response of the network request.
     * @param {*} config - request config
     * @param {*} response - network response
     */
    errorResponseFormatter( config, response ) {
		this.dispatchMBTransactionEvent( config, { type: 'ERROR', id: this.id } ); // send message-bus 'ERROR' event

        // get HTTP status
		let status = get( response, 'status', 0 );
		const status401 = toString( response ).match( '401' );
		const status403 = toString( response ).match( '403' );

		if( !isEmpty( status401 ) ) {
			status = 401;
		} else if( !isEmpty( status403 ) ) {
			status = 403;
		}

        // response structure
        const res = {
            error: get( response, 'status' ) ? 'HTTP_ERROR' : 'NETWORK_ERROR',
            status: status,
            body: get( response, 'data' ),
            headers: get( response, 'headers', {} ),
            timestamp: Date.now()
        };

        // add labels and urls to the store
		addLabelsAndUrlsOnStore( { response: response.data } );

        // dispatch `401/403` events
		this.dispatchAuthErrorEvent( res );

        return res;
    }

    /**
     * @desc Dispatch a message-bus event.
     * @param {*} config - request config
     * @param {*} payload - payload of the event
     */
    dispatchMBTransactionEvent( config, payload ) {
        if( true === config.emitEvent ) {
            const eventName = get( config, 'eventName', CORE_CONSTANTS.EVENT_HTTP_REQUEST_TRANSACTION );
            MessageBus.trigger( eventName, { ...payload, eventName: eventName } );
        }
    }

    /**
     * @desc Dispatch a message-bus event.
     * @param {*} payload - response payload of the event
     */
    dispatchNoPermissionsEvent( response ) {

        if( isPlainObject( response ) ) { // check if valid object response

            // check if template present in 'component.data.template' and is 'noPermission'
            if( has( response, 'data.component.data.template' ) && 'noPermission' === get( response, 'data.component.data.template', '' ) ) {

                // default overlay payload
                const defaultOverlaySettings = {
                    modalType: 'ERROR_NO_PERMISSIONS', // type of the modal view to render (using `overlayViewResolver` service)
                    showCloseButton: true, // show close button to close the overlay
                    enablePadding: false, // to add default padding provided by the overlay
                    stack: true, // render overlay with on top of another opened overlay (stack format)
                    hideOverlay: false, // hide overlay background
                    transparentOverlay: false, //render `overlay` with transparent background
                    transparentBackground: false, //render `view` with transparent background
                    onClose: () => {}, // close popover window callback
                    fixedToCenter: false, // place inner content to center
                    fixedToBottom: false, // place inner content to bottom
                    renderInfoIcon: false, // hide info icon
                    ignoreOverflow: false, // do not use overflow
                    ignoreOutsideClick: false, // show close button to close the overlay
                };

                // trigger opening of the overlay
                MessageBus.trigger( 'OPEN_OVERLAY', {
                    settings: defaultOverlaySettings, // provide default options
                    data: {}, // data used by the overlay modal view as props
                    config: {}, // config used for overlay modal view configuration.
                } );


            }
        }

    }

    /**
     * @desc Dispatch message-bus event for unauthenticated or unauthorized events.
     * @param {*} response - network response
     */
    dispatchAuthErrorEvent( response ) {
        if( includes( [ 401, 403 ], response.status ) ) {
            this.dispatchMBTransactionEvent( {
                emitEvent: true,
                eventName: CORE_CONSTANTS.EVENT_HTTP_AUTH_ERROR
            }, {
                statusCode: response.status
            } );
        }
    }

    /******************************************/

    /**
     * @desc Send HTTP `GET` request
     * @param {*} config - request config
     */
    get( config ) {
        const newConfig = this.makeRequestConfig( 'GET', config );
        return this.execute( newConfig );
    }

    /**
     * @desc Send HTTP `POST` request
     * @param {*} config - request config
     */
    post( config ) {
        const newConfig = this.makeRequestConfig( 'POST', config );
        return this.execute( newConfig );
    }

    /**
     * @desc Send HTTP `PUT` request
     * @param {*} config - request config
     */
    put( config ) {
        const newConfig = this.makeRequestConfig( 'PUT', config );
        return this.execute( newConfig );
    }

    /**
     * @desc Send HTTP `DELETE` request
     * @param {*} config - request config
     */
    delete( config ) {
        const newConfig = this.makeRequestConfig( 'DELETE', config );
        return this.execute( newConfig );
    }

    /**
     * @desc Send HTTP `GET` request
     * @param {*} config - request config
     */
    cancel() {

        // dispatch message-bus events
        this.dispatchMBTransactionEvent( { emitEvent: true, eventName: CORE_CONSTANTS.EVENT_HTTP_NETWORK_CANCEL }, { type: 'CANCEL', id: this.id } );

        // cancel request from the `http` module
        return Http.cancel( this.id );
    }

    /**
     * @desc Send HTTP `GET` request
     * @param {*} config - request config
     */
    cancelAll() {

        // cancel call requests from the `http` module
        return Http.cancelAll();
    }
}
